import React from 'react';
import { Link } from 'react-router-dom';
import { StandardTableActions, ColoredBadge, Select } from '../../components/ui';
import { Column } from '../../components/DataTable/DataTable';
import { TruncatedText } from '../../components/DataTable/TruncatedText';
import { Package } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, formatDateTime, APP_CONFIG } from '../../config/appConfig';

const PACKAGE_STATUS_COLOR_OPEN = '#22c55e';
const PACKAGE_STATUS_COLOR_CLOSED = '#64748b';

const renderPackageStatusBadge = (isOpen?: boolean, className?: string) => (
  <ColoredBadge
    label={isOpen ? 'Open' : 'Closed'}
    color={isOpen ? PACKAGE_STATUS_COLOR_OPEN : PACKAGE_STATUS_COLOR_CLOSED}
    className={className}
  />
);

const renderPackageTypeBadge = (label?: string, color?: string) => (
  <ColoredBadge label={label} color={color} />
);

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Packages',
    description: 'Manage your packages database',
    permissionModuleKey: 'packages',
    route: {
      list: '/packages',
      new: '/packages/new',
      detail: (id: number) => `/packages/${id}`,
      edit: (id: number) => `/packages/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/crm-packages',
      detail: (id: number) => `/crm-packages/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search packages by description, client, or type...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'packageDescr', 'clientId', 'packageTypeId', 'packageCategoryId', 'isOpen'
      ],
    },
    {
      name: 'dates',
      label: 'Dates',
      icon: '📅',
      fields: [
        'packageStartDate', 'packageEndDate'
      ],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'packageDescr',
        label: 'Description',
        placeholder: 'All Description',
        type: 'text' as const,
        getValue: (item: Package) => item.packageDescr,
        options: [],
      },
      {
        key: 'clientId',
        label: 'Client',
        placeholder: 'All Clients',
        type: 'select' as const,
        getValue: (item: Package) => 
          item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : ''),
        options: [],
      },
      {
        key: 'packageTypeId',
        label: 'Package Type',
        placeholder: 'All Types',
        type: 'select' as const,
        getValue: (item: Package) => 
          item.packageType?.pst_descr || (item.packageTypeId ? `Type #${item.packageTypeId}` : ''),
        options: [],
      },
      {
        key: 'packageCategoryId',
        label: 'Category',
        placeholder: 'All Categories',
        type: 'select' as const,
        getValue: (item: Package) => 
          item.packageCategory?.psc_descr || (item.packageCategoryId ? `Category #${item.packageCategoryId}` : ''),
        options: [],
      },
      {
        key: 'isOpen',
        label: 'Status',
        placeholder: 'All Status',
        type: 'boolean' as const,
        getValue: (item: Package) => item.isOpen ? 'Open' : 'Closed',
        options: ['Open', 'Closed'],
      },
      {
        key: 'packageStartDate',
        label: 'Start Date',
        placeholder: 'All Start Date',
        type: 'date' as const,
        getValue: (item: Package) =>
          item.packageStartDate
            ? formatDateGlobal(item.packageStartDate)
            : undefined,
        options: [],
      },
      {
        key: 'packageEndDate',
        label: 'End Date',
        placeholder: 'All End Date',
        type: 'date' as const,
        getValue: (item: Package) =>
          item.packageEndDate
            ? formatDateGlobal(item.packageEndDate)
            : undefined,
        options: [],
      },
    ],
    // Filter logic functions
    applyFilter: (
      items: Package[],
      filters: {
        packageDescr: string[] | string;
        clientId: string[];
        packageTypeId: string[];
        packageCategoryId: string[];
        isOpen: string[];
        packageStartDate: string[];
        packageEndDate: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Description filter
        const descrFilter = filters.packageDescr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.packageDescr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.packageDescr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Client filter
        if (filters.clientId && filters.clientId.length > 0) {
          const displayValue = item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : '');
          const matches = filters.clientId.some((filter) => {
            return displayValue === filter || 
                   (item.clientId && filter === `Client #${item.clientId}`) ||
                   (item.client?.clientName && filter === item.client.clientName);
          });
          if (!matches) return false;
        }

        // Package Type filter
        if (filters.packageTypeId && filters.packageTypeId.length > 0) {
          const displayValue = item.packageType?.pst_descr || (item.packageTypeId ? `Type #${item.packageTypeId}` : '');
          const matches = filters.packageTypeId.some((filter) => {
            return displayValue === filter ||
                   (item.packageTypeId && filter === `Type #${item.packageTypeId}`) ||
                   (item.packageType?.pst_descr && filter === item.packageType.pst_descr);
          });
          if (!matches) return false;
        }

        // Package Category filter
        if (filters.packageCategoryId && filters.packageCategoryId.length > 0) {
          const displayValue = item.packageCategory?.psc_descr || (item.packageCategoryId ? `Category #${item.packageCategoryId}` : '');
          const matches = filters.packageCategoryId.some((filter) => {
            return displayValue === filter ||
                   (item.packageCategoryId && filter === `Category #${item.packageCategoryId}`) ||
                   (item.packageCategory?.psc_descr && filter === item.packageCategory.psc_descr);
          });
          if (!matches) return false;
        }

        // Status filter
        if (filters.isOpen && filters.isOpen.length > 0) {
          const status = item.isOpen ? 'Open' : 'Closed';
          if (!filters.isOpen.includes(status)) return false;
        }

        // Start Date filter
        if (filters.packageStartDate.length > 0) {
          if (ranges.packageStartDate) {
            const range = ranges.packageStartDate;
            if (range.start && range.end && item.packageStartDate) {
              const itemDate = new Date(item.packageStartDate);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.packageStartDate
              ? formatDateGlobal(item.packageStartDate)
              : '';
            const matches = filters.packageStartDate.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // End Date filter
        if (filters.packageEndDate.length > 0) {
          if (ranges.packageEndDate) {
            const range = ranges.packageEndDate;
            if (range.start && range.end && item.packageEndDate) {
              const itemDate = new Date(item.packageEndDate);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.packageEndDate
              ? formatDateGlobal(item.packageEndDate)
              : '';
            const matches = filters.packageEndDate.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        return true;
      });
    },
  },

  // Toolbar Actions Configuration
  toolbar: {
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }

              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Package',
        showTitle: false,
        variant: 'primary' as const,
        href: '/packages/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Package) => {
      window.location.href = `/packages/${item.id}`;
    },
    keyExtractor: (item: Package) => item.id,
    getClientId: (item: Package) => item.clientId ?? null,
  },

  // Empty States
  emptyStates: {
    noData: 'Get started by creating a new package.',
    noSearchResults: 'No packages found matching your search criteria.',
    noFilterResults: 'No packages match the selected filters.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete Package',
    message: 'Are you sure you want to delete this package? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete package. Please try again.',
    handler: async (id: number) => {
      const { deletePackage } = await import('../../services/mutations');
      await deletePackage(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
    formatDateTime: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateTime(dateString);
    },
  },

  // Card View Configuration
  cardView: {
    getId: (item: Package) => item.id,
    getTitle: (item: Package) => `Package #${item.id}`,
    getSubtitle: (item: Package) => item.packageDescr,
    getStatusBadge: (item: Package) => ({
      label: item.isOpen ? 'Open' : 'Closed',
      variant: (item.isOpen ? 'success' : 'default') as 'default' | 'success' | 'warning' | 'danger' | 'info',
    }),
    getCardFields: (item: Package) => {
      const fields: Array<{
        label?: string;
        value: React.ReactNode;
        icon?: React.ReactNode;
        badge?: boolean;
        badgeVariant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'blue' | 'purple';
      }> = [];

      // Client
      if (item.client?.clientName) {
        fields.push({
          label: 'Client',
          value: item.client.clientName,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          ),
        });
      }

      // Package Type
      if (item.packageType?.pst_descr) {
        fields.push({
          label: 'Type',
          value: item.packageType.pst_descr,
          badge: true,
          badgeVariant: 'blue' as const,
        });
      }

      // Package Category
      if (item.packageCategory?.psc_descr) {
        fields.push({
          label: 'Category',
          value: item.packageCategory.psc_descr,
          badge: true,
          badgeVariant: 'purple' as const,
        });
      }

      // Dates
      if (item.packageStartDate || item.packageEndDate) {
        const dateStr = [
          item.packageStartDate ? formatDateGlobal(item.packageStartDate) : '',
          item.packageEndDate ? formatDateGlobal(item.packageEndDate) : '',
        ].filter(Boolean).join(' - ');
        fields.push({
          label: 'Dates',
          value: dateStr,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          ),
        });
      }

      // Minutes (remaining & total)
      if (item.packageMinutes !== undefined || item.remainingMinutes !== undefined) {
        const total = item.packageMinutes ?? 0;
        const remaining = item.remainingMinutes ?? (total - (item.totalInvoicedMinutes || 0));

        // Remaining minutes badge with color cues
        fields.push({
          label: 'Remaining',
          value: `${remaining} min`,
          badge: true,
          badgeVariant:
            remaining < 0 ? ('danger' as const)
            : remaining === 0 ? ('warning' as const)
            : ('success' as const),
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        });

        if (item.packageMinutes !== undefined) {
          fields.push({
            label: 'Total',
            value: `${total} min`,
          });
        }
      }

      // Linked tasks summary
      const linkedCount = item.crm_packages_tasks?.filter((pt) => pt.task).length ?? 0;
      fields.push({
        label: 'Linked tasks',
        value: linkedCount === 0 ? 'No tasks linked' : `${linkedCount} task${linkedCount === 1 ? '' : 's'} linked`,
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12M8 12h12M8 17h12M4 7h.01M4 12h.01M4 17h.01" />
          </svg>
        ),
      });

      return fields;
    },
    statusFilter: {
      enabled: true,
      defaultFilter: 'open',
      options: [
        { value: 'all', label: 'All Packages' },
        { value: 'open', label: 'Open Only' },
        { value: 'closed', label: 'Closed Only' },
      ],
      getStatus: (item: Package) => item.isOpen ? 'open' : 'closed',
    },
    updateStatus: async (id: number, newStatus: any) => {
      const { updatePackage } = await import('../../services/mutations');
      await updatePackage(id, { isOpen: newStatus });
    },
    renderStatusSelector: (item: Package, isUpdating: boolean, onStatusChange: (newStatus: any) => void) => {
      return (
        <Select
          value={item.isOpen ? 'open' : 'closed'}
          onChange={(e: any) => {
            onStatusChange(e.target.value === 'open');
          }}
          disabled={isUpdating}
          className="w-full text-xs"
          showClearButton={false}
          options={[
            { value: 'open', label: 'Open' },
            { value: 'closed', label: 'Closed' },
          ]}
        />
      );
    },
    actions: {
      view: true,
      edit: true,
    },
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Package Details',
    notFoundTitle: 'Package not found',
    notFoundMessage: 'The package you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Packages',
    defaultTitle: 'Unnamed Package',
    getTitle: (item: Package) => {
      return item.packageDescr || `Package #${item.id}`;
    },
  },

  // Form Configuration
  form: {
    new: {
      title: 'New Package',
      subtitle: 'Add a new package to your database',
      submitText: 'Create Package',
    },
    edit: {
      title: 'Edit Package',
      submitText: 'Update Package',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading package...',
    errorMessage: 'Failed to save package. Please try again.',
    create: async (data: Partial<Package>) => {
      const { createPackage } = await import('../../services/mutations');
      return await createPackage(data);
    },
    update: async (id: number, data: Partial<Package>) => {
      const { updatePackage } = await import('../../services/mutations');
      return await updatePackage(id, data);
    },
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'packageDescr',
      label: 'Description',
      type: 'text',
      required: true,
      placeholder: 'Enter package description',
      validation: {
        required: true,
        minLength: 2,
        maxLength: 255,
      },
    },
    {
      name: 'clientId',
      label: 'Client',
      type: 'select',
      placeholder: 'Select a client',
      options: [],
      apiEndpoint: '/crm-clients',
      labelField: 'clientName',
      required: true,
      validation: {
        required: true,
      },
    },
    {
      name: 'packageTypeId',
      label: 'Package Type',
      type: 'select',
      placeholder: 'Select package type (optional)',
      options: [],
      apiEndpoint: '/p-crm-service-type',
      labelField: 'pst_descr',
    },
    {
      name: 'packageCategoryId',
      label: 'Category',
      type: 'select',
      placeholder: 'Select category (optional)',
      options: [],
      apiEndpoint: '/p-crm-service-category',
      labelField: 'psc_descr',
    },
    {
      name: 'packageStartDate',
      label: 'Start Date',
      type: 'date',
      placeholder: 'Select start date',
    },
    {
      name: 'packageEndDate',
      label: 'End Date',
      type: 'date',
      placeholder: 'Select end date',
    },
    {
      name: 'packageMinutes',
      label: 'Package Minutes',
      type: 'number',
      placeholder: 'Enter package minutes',
      validation: {
        min: 0,
      },
    },
    {
      name: 'isOpen',
      label: 'Status',
      type: 'switch',
      switchLabel: 'Package is open',
      description: 'Check if this package is currently open',
      labelPosition: 'left',
      defaultValue: true,
    },
  ] as FieldConfig[],

  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        icon: '📦',
        gridCols: 'md:grid-cols-2',
        fields: ['packageDescr', 'clientId'],
        collapsible: true,
      },
      {
        id: 'type',
        title: 'Type & Category',
        icon: '🏷️',
        gridCols: 'md:grid-cols-2',
        fields: ['packageTypeId', 'packageCategoryId'],
        collapsible: true,
      },
      {
        id: 'dates',
        title: 'Dates',
        icon: '📅',
        gridCols: 'md:grid-cols-2',
        fields: ['packageStartDate', 'packageEndDate'],
        collapsible: true,
      },
      {
        id: 'details',
        title: 'Package Details',
        icon: '📋',
        gridCols: 'md:grid-cols-2',
        fields: ['packageMinutes', 'isOpen'],
        collapsible: true,
      },
    ],
  } as FormLayout,
};

// Helper function to get columns (formatDate, _arg2?, colors?, listContext?)
export const getColumns = (
  formatDate: (dateString?: string) => string,
  _arg2?: unknown,
  colors?: { text?: string; textMuted?: string; surface?: string; border?: string },
  listContext?: { onOpenLinkedTasksPanel?: (item: Package) => void }
): Column<Package>[] => [
  {
    key: 'packageDescr',
    header: 'Description',
    sortable: true,
    render: (value, row) => {
      const clientName = row.client?.clientName || (row.clientId ? `Client #${row.clientId}` : '');
      return (
        <div className="min-w-0">
          <div>
            <TruncatedText
              text={value || ''}
              maxLength={80}
              showTooltip={true}
            />
          </div>
          {clientName && (
            <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 truncate">
              {clientName}
            </div>
          )}
        </div>
      );
    },
  },
  {
    key: 'packageTypeId',
    header: 'Type',
    sortable: true,
    render: (_value, row) => renderPackageTypeBadge(row.packageType?.pst_descr, row.packageType?.pst_color),
  },
  {
    key: 'packageCategoryId',
    header: 'Category',
    sortable: true,
    render: (value, row) => (
      row.packageCategory?.psc_descr || '-'
    ),
  },
  {
    key: 'packageStartDate',
    header: 'Start Date',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'packageEndDate',
    header: 'End Date',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'remainingMinutes',
    header: 'Remaining',
    sortable: true,
    render: (value) => (
      <span className="block text-center">{value != null ? `${value} min` : '-'}</span>
    ),
  },
  {
    key: 'crm_packages_tasks',
    header: 'Linked Tasks',
    sortable: false,
    render: (_value, item) => {
      const count = item.crm_packages_tasks?.length ?? 0;
      const onOpenPanel = listContext?.onOpenLinkedTasksPanel;
      if (onOpenPanel) {
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onOpenPanel(item);
            }}
            className="text-indigo-600 hover:text-indigo-800 hover:underline text-left"
          >
            {count === 0 ? '0 tasks' : `${count} task${count !== 1 ? 's' : ''}`}
          </button>
        );
      }
      if (count === 0) return '-';
      return (
        <Link
          to={moduleConfig.module.route.detail(item.id)}
          className="text-indigo-600 hover:text-indigo-800 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {count} task{count !== 1 ? 's' : ''}
        </Link>
      );
    },
  },
  {
    key: 'isOpen',
    header: 'Status',
    sortable: true,
    render: (_value, row) => renderPackageStatusBadge(row.isOpen),
  },
];

// Helper function to get detail sections
export const getDetailSections = (
  item: Package,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      fields: [
        {
          label: 'Package ID',
          value: item.id?.toString() || '-',
        },
        {
          label: 'Description',
          value: item.packageDescr ? (
            <TruncatedText
              text={item.packageDescr}
              maxLength={200}
              className="text-sm text-gray-900"
              showTooltip={true}
            />
          ) : '-',
        },
        {
          label: 'Client',
          value: item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : '-'),
        },
        {
          label: 'Status',
          value: renderPackageStatusBadge(item.isOpen),
        },
      ],
    },
    {
      title: 'Type & Category',
      fields: [
        {
          label: 'Package Type',
          value:
            item.packageType?.pst_descr
              ? renderPackageTypeBadge(item.packageType.pst_descr, item.packageType.pst_color)
              : (item.packageTypeId ? `Type #${item.packageTypeId}` : '-'),
        },
        {
          label: 'Category',
          value: item.packageCategory?.psc_descr || (item.packageCategoryId ? `Category #${item.packageCategoryId}` : '-'),
        },
      ],
    },
    {
      title: 'Dates',
      fields: [
        {
          label: 'Start Date',
          value: formatDate(item.packageStartDate),
        },
        {
          label: 'End Date',
          value: formatDate(item.packageEndDate),
        },
      ],
    },
    {
      title: 'Package Details',
      fields: [
        {
          label: 'Remaining',
          value: (
            <span className="block text-center">
              {item.remainingMinutes != null ? `${item.remainingMinutes} minutes` : '-'}
            </span>
          ),
        },
      ],
    },
    {
      title: 'Linked Package Tasks',
      fields: [
        {
          label: 'Tasks',
          value: (() => {
            const links = item.crm_packages_tasks?.filter((pt) => pt.task) ?? [];
            if (links.length === 0) {
              return 'No linked tasks';
            }
            return (
              <ul className="list-disc list-inside space-y-2">
                {links.map((pt) => {
                  const t = pt.task!;
                  const desc = t.taskDescription?.slice(0, 60) ?? `Task #${t.taskId}`;
                  const status = t.taskStatusDescr?.ts_descr ?? '';
                  const date = t.taskDate ? formatDate(t.taskDate) : '';
                  const invoiced = t.taskInvoicedMin != null ? `${t.taskInvoicedMin} min` : '';
                  const sub = [status, date, invoiced].filter(Boolean).join(' · ');
                  return (
                    <li key={pt.id}>
                      <Link
                        to={`/crm-tasks/${t.taskId}`}
                        className="text-indigo-600 hover:text-indigo-800 hover:underline"
                      >
                        {desc}{desc.length >= 60 ? '…' : ''}
                      </Link>
                      {sub && <span className="block text-sm text-gray-500 ml-6">{sub}</span>}
                    </li>
                  );
                })}
              </ul>
            );
          })(),
        },
      ],
    },
  ];
};

// Helper function to render toolbar icon
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    case 'cardboard':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to render table action buttons
export const getTableActions = (
  item: Package,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};


export const getInitialFormData = (): Partial<Package> => {
  return {
    packageDescr: '',
    clientId: undefined,
    packageTypeId: undefined,
    packageCategoryId: undefined,
    packageStartDate: undefined,
    packageEndDate: undefined,
    packageMinutes: undefined,
    isOpen: true,
  };
};

// Helper function to map item data to form data
export const mapItemToFormData = (item: Package): Partial<Package> => {
  return {
    packageDescr: item.packageDescr || '',
    clientId: item.clientId || undefined,
    packageTypeId: item.packageTypeId || undefined,
    packageCategoryId: item.packageCategoryId || undefined,
    packageStartDate: item.packageStartDate || undefined,
    packageEndDate: item.packageEndDate || undefined,
    packageMinutes: item.packageMinutes || undefined,
    isOpen: item.isOpen ?? true,
  };
};

export type ModuleConfig = typeof moduleConfig;

